import React, { useEffect } from 'react';
import { Divider, Grid, Typography, Button, IconButton } from '@mui/material';
import AudioWaveform from './AudioWaveform';
import { LargeMicIcon, VibrationSvg } from '../../assets/images/UI-dashboard';
import { ResultTable } from './ResultTable';
import { useSpeechRecognition } from '../../features/SpeechRecognition/useSpeechRecognition';
import { useAppDispatch, useAppSelector } from '../../state/hooks';
import {
  processAudio,
  resetAudio,
  setEnableAudioMappingTable,
  validateMappings,
} from '../../features/SpeechRecognition/audioSlice';
import { RootState } from '../../state/store';
import Spinner from '../UI/Spinner';
import {
  fetchNLPReviewTableDataThunk,
  setNlpResultTrue,
  toggleEnableReviewTableDialog,
} from '../../pages/ScenarioCreation/components/ReviewTable/reviewTableSlice';
import { InfoRounded } from '@mui/icons-material';
import { hideLoader, showLoader } from '../../features/ui/loaderSlice';
import {
  AudioSyncContainer,
  CancelButton,
  ConfirmButton,
  IncorrectText,
  SyncHeading,
  TapText,
  TranscriptText,
  ValidateButton,
} from './ResultTable.Styled';
import CustomButton from '../UI/CustomButton/CustomButton';
import { ColorFullText } from './AudioWaveform.Styled';

interface AudioRecorderProps {
  onClose: () => void;
}

const VITE_APP_BASE_URL = import.meta.env.VITE_APP_BASE_URL;

const conditionEnvCheck = () => {
  if (VITE_APP_BASE_URL === 'https://ko-india-dev-be.azurewebsites.net/api/') {
    return true;
  } else {
    return false;
  }
};

export const AudioRecorder: React.FC<AudioRecorderProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const { audioBlob, isListening, startListening, stopListening } = useSpeechRecognition();

  useEffect(() => {
    if (audioBlob) {
      handleSend();
      if (conditionEnvCheck() === true) {
        handleDownload();
      }
    }
  }, [audioBlob]);

  const handleSend = async () => {
    if (audioBlob) {
      const formData = new FormData();
      formData.append('audio', audioBlob);
      dispatch(processAudio(formData));
    }
  };

  const handleDownload = () => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob);
      const link = document.createElement('a');
      link.download = 'recording.wav';
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const transcription = useAppSelector((state: RootState) => state.audio.transcription);
  const loader = useAppSelector((state: RootState) => state.audio.loading);
  const error = useAppSelector((state: RootState) => state.audio.error);
  const mappings = useAppSelector((state: RootState) => state.audio.mappings);
  const reviewTableMappings = useAppSelector((state: RootState) => state.reviewTable.mappings);

  const handleAudioResultTableReset = () => {
    dispatch(resetAudio());
  };

  const handleConfirmation = async () => {
    onClose();
    const payload = {
      mapping: mappings,
      review_table_mapping: reviewTableMappings,
    };
    await dispatch(showLoader());
    await dispatch(fetchNLPReviewTableDataThunk(payload));
    await dispatch(toggleEnableReviewTableDialog(true));
    await dispatch(setEnableAudioMappingTable(true));
    await dispatch(setNlpResultTrue());
    await dispatch(hideLoader());
  };

  const handleValidate = () => {
    dispatch(validateMappings(mappings));
  };

  const isConfirmEnabled = mappings.length > 0 && mappings.every((mapping) => mapping.Valid);

  const shouldShowErrorMessage = (mappings: any) => {
    return mappings.length > 0 && !mappings.every((mapping: any) => mapping.Invalid_Column.length === 0);
  };

  return (
    <>
      <AudioSyncContainer>
        <Grid
          item
          container
          direction="column"
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ColorFullText>
            You are now interacting with a Smart AI. Your data will be shared to Whisper for processing.
          </ColorFullText>
          <Grid display="flex" mt="10px">
            <img src={VibrationSvg} alt="waves" style={{ cursor: 'pointer', marginRight: 8 }} />
            <SyncHeading>Try Saying</SyncHeading>
          </Grid>

          <Grid container direction="column" alignItems="center" style={{ maxHeight: '450px', overflowY: 'hidden' }}>
            <TranscriptText>
              {transcription.length > 0 ? transcription[transcription.length - 1].text : ''}
            </TranscriptText>
            {isListening ? (
              <Grid
                mt="10px"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <AudioWaveform />
                <Button variant="contained" color="secondary" onClick={stopListening} style={{ marginTop: 20 }}>
                  Stop Recording
                </Button>
              </Grid>
            ) : (
              <>
                <img onClick={startListening} src={LargeMicIcon} alt="lg-mic-icon" style={{ marginTop: '10px' }} />
                <TapText>Tap the mic to talk</TapText>
              </>
            )}
          </Grid>
          <Grid
            container
            direction="column"
            style={{
              maxHeight: '450px',
              overflowY: 'auto',
            }}
          >
            <Grid style={{ display: 'flex', justifyContent: 'space-between', alignContent: 'end', alignItems: 'end' }}>
              <Typography style={{ fontSize: '14px', fontWeight: '700' }}>Scenario Input</Typography>

              {conditionEnvCheck() === true && (
                <CustomButton
                  fullWidth
                  style={{
                    marginTop: '10px',
                    color: 'white',
                    width: '86.18px',
                    height: '30.5px',
                    fontSize: '11.48px',
                    lineHeight: '18.87px',
                    fontWeight: 400,
                    whiteSpace: 'nowrap',
                  }}
                  onClick={handleDownload}
                >
                  Download
                </CustomButton>
              )}
              <CustomButton
                fullWidth
                style={{
                  marginTop: '10px',
                  color: 'white',
                  width: '86.18px',
                  height: '30.5px',
                  fontSize: '11.48px',
                  lineHeight: '18.87px',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                }}
                onClick={handleAudioResultTableReset}
              >
                Reset Table
              </CustomButton>
            </Grid>
            <Divider style={{ margin: '10px 0' }} />
            {loader === true ? <Spinner show={true} /> : <ResultTable mappings={mappings} error={error} />}
          </Grid>
        </Grid>
      </AudioSyncContainer>
      <Grid container direction="column" justifyContent="flex-end" style={{ flex: 1 }}>
        <Grid
          container
          item
          style={{
            padding: '10px 0',
            justifyContent: 'space-between',
            marginTop: '5px',
          }}
        >
          <Grid item>
            <Grid
              style={{
                justifyContent: 'end',
                alignContent: 'flex-end',
                alignItems: 'flex-end',
              }}
              item
            >
              {shouldShowErrorMessage(mappings) && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <IconButton>
                    <InfoRounded
                      style={{
                        color: 'white',
                        background: '#Df2020',
                        width: '17px',
                        height: '17px',
                        borderRadius: '50%',
                      }}
                    />
                  </IconButton>
                  <IncorrectText>
                    Incorrect entries found in Columns. Please edit or delete before proceeding.
                  </IncorrectText>
                </div>
              )}
            </Grid>
          </Grid>
          <Grid item>
            <CancelButton onClick={onClose}>Cancel</CancelButton>
            <ValidateButton onClick={handleValidate}>Validate</ValidateButton>
            <ConfirmButton
              onClick={handleConfirmation}
              disabled={!isConfirmEnabled}
              sx={{
                background: !isConfirmEnabled ? '#7d7d7d' : '#E21824',
                color: 'white !important',
                opacity: !isConfirmEnabled ? '0.7' : '1',
                '&:hover': {
                  background: !isConfirmEnabled ? '#7d7d7d' : '#E21824',
                },
              }}
            >
              Confirm
            </ConfirmButton>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default AudioRecorder;
